import { Button, DatePicker, Drawer, Form, Input } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import type {
  PatchedPublicAccessCredentialUpdate,
  PublicAccessCredentialCreate,
  PublicAccessCredentialRead
} from 'src/api/main'

import { AppBindingsField } from './AppBindingsField'
import type { BindingDraft } from './types'

type FormValues = {
  name: string
  expiresAt?: Dayjs | null
  apps?: BindingDraft[]
}

type Props = {
  open: boolean
  mode: 'create' | 'edit'
  credential: PublicAccessCredentialRead | null
  apps: WorkspaceApp[]
  appsLoading: boolean
  submitting: boolean
  /** Optional — pass only when `mode` may be `'create'`. */
  onCreate?: (body: PublicAccessCredentialCreate) => void
  /** Optional — pass only when `mode` may be `'edit'`. */
  onUpdate?: (id: string, body: PatchedPublicAccessCredentialUpdate) => void
  onClose: () => void
}

/**
 * Create + edit drawer for public-access credentials. Both modes edit the full
 * payload — name, expiry, and app bindings (apps + per-app permissions). Edit
 * prefills the bindings by reverse-mapping the credential's current bindings
 * back to workspace apps via `saas_app_token`.
 */
export const PublicAccessCredentialDrawer = ({
  open,
  mode,
  credential,
  apps,
  appsLoading,
  submitting,
  onCreate,
  onUpdate,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()

  const isEdit = mode === 'edit'

  useEffect(() => {
    if (!open) return
    if (isEdit && credential) {
      const tokenToApp = new Map(apps.map((a) => [a.token, a]))
      form.setFieldsValue({
        name: credential.name,
        expiresAt: credential.expires_at ? dayjs(credential.expires_at) : null,
        // Reverse-map current bindings to editable rows; drop any whose app is
        // no longer in the workspace list (can't resolve back to an `appId`).
        apps: (credential.apps ?? [])
          .map((b) => {
            const app = tokenToApp.get(b.saas_app_token)

            return app
              ? { appId: app.id, permission_codes: [...b.permission_codes] }
              : null
          })
          .filter((d): d is BindingDraft => d !== null)
      })
    } else {
      form.resetFields()
    }
  }, [open, isEdit, credential, apps, form])

  const handleFinish = (values: FormValues) => {
    const expires_at = values.expiresAt ? values.expiresAt.toISOString() : null

    const appsById = new Map(apps.map((a) => [a.id, a]))
    const bindings = (values.apps ?? [])
      .map((b) => ({ binding: b, app: appsById.get(b.appId) }))
      // Skip rows whose app can't be resolved to real identifiers; the form
      // validator already rejects this case, so this is defence-in-depth
      // against ever POSTing an empty `app_code`/`saas_app_token`.
      .filter(({ app }) => app?.instanceTypeCode && app.token)
      .map(({ binding, app }) => ({
        // `app_code` has no dedicated field on the app model; we map it to
        // the instance-type code.
        app_code: app!.instanceTypeCode!,
        saas_app_token: app!.token!,
        permission_codes: binding.permission_codes ?? []
      }))

    if (isEdit && credential) {
      onUpdate?.(credential.id, {
        name: values.name,
        expires_at,
        apps: bindings
      })

      return
    }

    onCreate?.({ name: values.name, expires_at, apps: bindings })
  }

  return (
    <Drawer
      title={
        isEdit
          ? t('workspace.publicAccess.editDrawerTitle')
          : t('workspace.publicAccess.createDrawerTitle')
      }
      open={open}
      size={460}
      destroyOnHidden
      onClose={onClose}
      extra={
        <Button
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          {isEdit ? t('save', 'Save') : t('workspace.publicAccess.create')}
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
      >
        <Form.Item
          label={t('name')}
          name="name"
          rules={[{ required: true, message: t('required_field_error') }]}
        >
          <Input placeholder={t('workspace.publicAccess.namePlaceholder')} />
        </Form.Item>

        <Form.Item
          label={t('workspace.publicAccess.expiresAt')}
          name="expiresAt"
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            placeholder={t('workspace.publicAccess.expiresAtPlaceholder')}
            disabledDate={(current) => current && current.isBefore(dayjs())}
          />
        </Form.Item>

        <Form.Item
          label={t('workspace.publicAccess.bindings.label')}
          name="apps"
          rules={[
            {
              validator: (_, val: BindingDraft[] | undefined) => {
                const selected = (val ?? []).filter((b) => b.appId)
                if (selected.length === 0) {
                  return Promise.reject(
                    new Error(t('workspace.publicAccess.bindings.required'))
                  )
                }
                const hasUnbindable = selected.some((b) => {
                  const app = apps.find((a) => a.id === b.appId)

                  return !app?.instanceTypeCode || !app.token
                })

                return hasUnbindable
                  ? Promise.reject(
                      new Error(
                        t('workspace.publicAccess.bindings.unbindableApp')
                      )
                    )
                  : Promise.resolve()
              }
            }
          ]}
        >
          <AppBindingsField apps={apps} appsLoading={appsLoading} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
